import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark';

type ThemeModeContextValue = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
};

const STORAGE_KEY = 'shell.theme.mode';
const ATTR = 'data-shell-theme';

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* ignore */
  }
  return 'light';
}

function applyDocumentAttr(mode: ThemeMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(ATTR, mode);
  document.documentElement.style.colorScheme = mode;
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const initial = readStoredMode();
    applyDocumentAttr(initial);
    return initial;
  });

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    applyDocumentAttr(next);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  // Подтягиваем атрибут на html на каждый mount (на случай SSR / refresh).
  useEffect(() => {
    applyDocumentAttr(mode);
  }, [mode]);

  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    // Грейсфул-фолбэк: если провайдер не подключён, читаем атрибут с html.
    const current =
      typeof document !== 'undefined' && document.documentElement.getAttribute(ATTR) === 'dark'
        ? 'dark'
        : 'light';
    return {
      mode: current,
      setMode: () => undefined,
      toggleMode: () => undefined,
    };
  }
  return ctx;
}
